import { bacteriaMetaConfig_9P } from '../calculate_ctvalue';
import { getBacteriaScore } from '../../utils/bacteriaEvaluator';

// =====================================================================
// Config_Neurotransmitter.js  —  9P panel "Neurotransmitter" summary section
// ---------------------------------------------------------------------
// Flow:
//   1. For each species, the ct value from the CSV becomes a score:
//        - if ct >= 35            -> 0            (hard override)
//        - otherwise use the label from calculate_ctvalue.js:
//            'Deficient' | 'Slightly Deficient' | 'Normal'
//          and convert it via neurotransmitter_9P_scoreMapping.
//   2. All species scores are summed (X).
//   3. X is compared against the conditions to get the result label:
//        X <= 3.5           -> Below optimum
//        3.5 < X < 4.825    -> Slightly below optimum
//        X >= 4.825         -> Optimum
//   4. The result is shown on page 22 (eng) / page 21 (thai).
// =====================================================================

// Species that are summed for this section.
// NOTE: these keys must match the CSV column / formData keys exactly.
const neurotransmitter_9P_sp = [
  'Bifidobacterium bifidum',
  'Bifidobacterium breve',
  'Bifidobacterium lactis',
  'Bifidobacterium longum',
  'Lactobacillus acidophilus',
  'Lactobacillus gasseri',
  'Lactobacillus paracasei',
  'Lactobacillus reuteri',
  'Lactobacillus rhamnosus',
  'Bacteroides genus', // "bacteroides"
];

// ---------------------------------------------------------------------
// Per-species LABEL -> score mapping.
// (Applied only when ct < 35; ct >= 35 always scores 0 — see helper.)
// Values taken from the provided table:
//                            Deficient  Slightly deficient  Normal
//   Bifidobacterium bifidum    0.25     0.5                 1
//   Bifidobacterium breve      0        0                   0
//   Bifidobacterium lactis     0.125    0.25                0.5
//   Bifidobacterium longum     0.5      1                   2
//   Lactobacillus acidophilus  0        0                   0
//   Lactobacillus gasseri      0.05     0.1                 0.2
//   Lactobacillus paracasei    0.25     0.5                 1
//   Lactobacillus reuteri      0.125    0.25                0.5
//   Lactobacillus rhamnosus    0.125    0.25                0.5
//   Bacteroides genus          0.25     0.5                 1
// ---------------------------------------------------------------------
const neurotransmitter_9P_scoreMapping = {
  'Bifidobacterium bifidum': {
    'Deficient': 0.25,
    'Slightly Deficient': 0.5,
    'Normal': 1,
  },
  'Bifidobacterium breve': {
    'Deficient': 0,
    'Slightly Deficient': 0,
    'Normal': 0,
  },
  'Bifidobacterium lactis': {
    'Deficient': 0.125,
    'Slightly Deficient': 0.25,
    'Normal': 0.5,
  },
  'Bifidobacterium longum': {
    'Deficient': 0.5,
    'Slightly Deficient': 1,
    'Normal': 2,
  },
  'Lactobacillus acidophilus': {
    'Deficient': 0,
    'Slightly Deficient': 0,
    'Normal': 0,
  },
  'Lactobacillus gasseri': {
    'Deficient': 0.05,
    'Slightly Deficient': 0.1,
    'Normal': 0.2,
  },
  'Lactobacillus paracasei': {
    'Deficient': 0.25,
    'Slightly Deficient': 0.5,
    'Normal': 1,
  },
  'Lactobacillus reuteri': {
    'Deficient': 0.125,
    'Slightly Deficient': 0.25,
    'Normal': 0.5,
  },
  'Lactobacillus rhamnosus': {
    'Deficient': 0.125,
    'Slightly Deficient': 0.25,
    'Normal': 0.5,
  },
  'Bacteroides genus': {
    'Deficient': 0.25,
    'Slightly Deficient': 0.5,
    'Normal': 1,
  },
};

// ---------------------------------------------------------------------
// Result conditions + on-page position — ENGLISH version (page 22).
// X = summed score across all species above.
// Adjust the Xpos/Ypos/image values to match your PDF layout.
// ---------------------------------------------------------------------
const neurotransmitter_9P_condition = {
  page: 21, // eng version page (same page as macronutrient)
    XposText: 505,
    YposText: 536,
    scale_imageX: 67,
    scale_imageY: 67,
    XposImage: 88,
    YposImage: 498,
    // Bar (color) positioning — 5 bars for visual; arrow lands on matched score (1-3).
    xBarStart: 174,
    YBarStart: 542,
    xBarGap: 64,
  conditions: [
    {
      condition: (x) => x <= 3.5,
      score: 1,
      label: 'Below optimum',
      image: 'd1',
    },
    {
      condition: (x) => x > 3.5 && x < 4.825,
      score: 2,
      label: 'Slightly below optimum',
      image: 'd2',
    },
    {
      condition: (x) => x >= 4.825,
      score: 3,
      label: 'Optimum',
      image: 'd3',
    },
  ],
};

// ---------------------------------------------------------------------
// Result conditions + on-page position — THAI version (page 21).
// Same thresholds, Thai labels + Thai-page positions.
// ---------------------------------------------------------------------
const neurotransmitter_9P_condition_thai = {
  page: 20, // thai version page (same page as macronutrient)
  XposText: 510,
  YposText: 535,
  scale_imageX: 67,
  scale_imageY: 67,
  XposImage: 88,
  YposImage: 500,
  // Bar (color) positioning — 5 bars for visual; arrow lands on matched score (1-3).
  xBarStart: 174,
  YBarStart: 545,
  xBarGap: 64,
  conditions: [
    {
      condition: (x) => x <= 3.5,
      score: 1,
      label: 'ต่ำกว่าระดับที่เหมาะสม',
      image: 'd1',
    },
    {
      condition: (x) => x > 3.5 && x < 4.825,
      score: 2,
      label: 'ต่ำกว่าระดับที่เหมาะสมเล็กน้อย',
      image: 'd2',
    },
    {
      condition: (x) => x >= 4.825,
      score: 3,
      label: 'เหมาะสม',
      image: 'd3',
    },
  ],
};

// ---------------------------------------------------------------------
// Helper: score for one species.
//   - ct >= 35            -> 0 (hard override)
//   - otherwise: label from calculate_ctvalue.js -> score via mapping
// Returns 0 when the species/label is unknown or the value is missing.
// ---------------------------------------------------------------------
function getNeurotransmitter9PSpeciesScore(species, formData) {
  const raw = parseFloat(formData?.[species]);
  if (isNaN(raw)) return 0;

  // hard override: ct >= 35 always scores 0
  if (raw >= 35) return 0;

  const meta = bacteriaMetaConfig_9P[species];
  if (!meta) return 0;

  // label computed by the same logic as the rest of the 9P report
  const { label } = getBacteriaScore(species, raw, formData, meta);

  const mapping = neurotransmitter_9P_scoreMapping[species];
  if (!mapping || mapping[label] === undefined) return 0;

  return mapping[label];
}

// ---------------------------------------------------------------------
// Main: sum all species scores and resolve the result label.
// Pass useThaiVersion=true to use the Thai config (page 21).
// Returns: { sum, label, image, score, config }
// ---------------------------------------------------------------------
function calculateNeurotransmitter9P(formData, useThaiVersion = false) {
  let sum = 0;

  neurotransmitter_9P_sp.forEach((species) => {
    sum += getNeurotransmitter9PSpeciesScore(species, formData);
  });

  // round to 4 decimals to avoid float noise
  sum = parseFloat(sum.toFixed(4));

  const config = useThaiVersion
    ? neurotransmitter_9P_condition_thai
    : neurotransmitter_9P_condition;

  const matched = config.conditions.find((c) => c.condition(sum));

  return {
    sum,
    label: matched ? matched.label : null,
    image: matched ? matched.image : null,
    score: matched ? matched.score : null,
    config,
  };
}

export {
  neurotransmitter_9P_sp,
  neurotransmitter_9P_scoreMapping,
  neurotransmitter_9P_condition,
  neurotransmitter_9P_condition_thai,
  getNeurotransmitter9PSpeciesScore,
  calculateNeurotransmitter9P,
};
